import {
  Badge,
  Box,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type {
  PublicBidDto,
  PublicBidPatchDto,
} from "../../../../../autogen/bff-api";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";
import { isPublicBidEditable } from "../publicBidEditability";

interface Props {
  bidId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

const limitedCoverLetterToolbar = {
  underline: false,
  headings: false,
  colors: false,
  link: false,
  table: false,
  image: false,
  template: false,
};

export const CoverLetterStep = ({
  bidId,
  previousStep,
  nextStep,
  nextStepLabel,
}: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<
    PublicBidDto,
    PublicBidPatchDto
  >(bidId);
  const readOnly = !isPublicBidEditable(state);
  const isSaving =
    fieldStatus("coverLetter") === "saving" ||
    fieldStatus("coverLetter") === "pending";

  return (
    <Box pb="8" pt="6">
      <Flex align="center" justify="space-between" gap="3" mb="6" flexWrap="wrap">
        <Stack spacing="1">
          <Flex align="center" gap="2" flexWrap="wrap">
            <Heading size="md">{t("Cover letter")}</Heading>
            <Badge
              bg="smBackgroundSecondary"
              color="smMuted"
              px="2"
              py="0.5"
              borderRadius="md"
              textTransform="none"
            >
              {t("Optional")}
            </Badge>
            {isSaving && <Spinner size="sm" color="smMuted" />}
          </Flex>
          <Text fontSize="sm" color="smMuted">
            {t(
              "Add an optional message to the contracting authority. It will be included in the offer report.",
            )}
          </Text>
        </Stack>
      </Flex>

      <Card variant="outline" rounded="xl" mb="8">
        <CardBody>
          <FormControl>
            <FormLabel fontSize="sm">{t("Message")}</FormLabel>
            <TipTap
              content={state.coverLetter ?? ""}
              onUpdate={(value) =>
                updateField({ coverLetter: value }, { debounceMs: 300 })
              }
              editable={!readOnly}
              toolbar={limitedCoverLetterToolbar}
              debounceWait={0}
            />
            <FormHelperText>
              {t("Keep the cover letter short. Attach files in the offer documents step.")}
            </FormHelperText>
          </FormControl>
        </CardBody>
      </Card>

      <StepNavigation
        onPrevious={previousStep}
        onNext={nextStep}
        nextLabel={nextStepLabel}
      />
    </Box>
  );
};
